<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <export
      url="/payout_additional/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="csvTitle"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    >
      <div class="mt-16 text-red text-body-big">
        {{ $t('toolbar.export.downloadPainFile.text') }}
      </div>
      <Multiselect
        v-model="painFileAccount"
        :options="painFileOptions"
        :label="$t('toolbar.export.downloadPainFile.dropdown.DEFAULT')"
        :can-deselect="false"
        :can-clear="false"
        class="mt-3 mb-5"
      />
      <export-link
        v-if="painFileAccount && checkedIdsList.length > 0"
        :custom-export="exportPainFile"
        :label="$t('toolbar.export.downloadPainFile.button')"
        class="mt-5"
      />
      <div v-else-if="checkedIdsList.length === 0">
        {{ $t('toolbar.export.downloadPainFile.noPayoutsSelected') }}
      </div>
    </export>
    <action
      v-if="checkedIdsList.length > 0"
      :icon="SuccessIcon"
      :label="t('pages.additionalPayments.bulkStatus.action')"
      @click="bulkStatusModalOpen = true"
    />
    <action
      :icon="AddIcon"
      :label="t('pages.additionalPayments.actions.create')"
      @click="createModalOpen = true"
    />
  </toolbar>
  <additional-payment-create-modal
    v-model:open="createModalOpen"
    @saved="refetch"
  />
  <additional-payment-create-modal
    v-model:open="editModalOpen"
    :variant="editModalVariant"
    :payment="selectedForEdit"
    @saved="refetch"
  />
  <additional-payment-bulk-status-change-modal
    v-model:open="bulkStatusModalOpen"
    :payout-ids="checkedIdsList"
    @saved="onBulkStatusSaved"
  />
  <create-app-payment-confirm-modal
    v-model:open="appPaymentConfirmOpen"
    :loading="appPaymentLoading"
    @confirm="executeCreateAppPayment"
  />
  <delete-additional-payment-confirm-modal
    v-model:open="deleteConfirmOpen"
    :loading="deleteLoading"
    :message="t('pages.additionalPayments.deleteConfirm')"
    @confirm="executeDelete"
  />
  <div class="table ml-12">
    <data-table
      class="mb-auto"
      v-model:sort-by="sortKey"
      v-model:sort-direction="sortDirection"
      :busy="loading"
      :items="records"
      :columns="columns"
      :visible-columns="visibleColumns"
      :actions="actions"
      :choosable="true"
      v-model:checked-ids-list="checkedIdsList"
    />
  </div>
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script setup lang="tsx">
import { computed, reactive, ref, unref } from 'vue';
import { format } from 'date-fns';
import { useI18n } from 'vue-i18n';
import FilterPane from '@components/utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@components/utils/toolbar/panes/AddFields.vue';
import Export from '@utils/toolbar/panes/Export.vue';
import ExportLink from '@utils/toolbar/panes/ExportLink.vue';
import Multiselect from '@components/utils/Multiselect.vue';
import Action from '@components/utils/toolbar/Action.vue';
import AddIcon from '@images/panes/add.svg?component';
import SuccessIcon from '@images/success.svg?component';
import Toolbar from '@components/utils/toolbar/Toolbar.vue';
import Pagination from '@components/utils/table/Pagination.vue';
import DataTable, { type Action as TableAction, type Column } from '@components/utils/table/Table.vue';
import { enumOptionsProvider, useProvider } from '@components/utils/table/hooks/provider';
import type { Filter } from '@/components/utils/filter/Filter.vue';
import type { PayoutAdditional, Download } from '@/entities/Entities';
import AdditionalPaymentCreateModal from '@pages/additionalPayments/AdditionalPaymentCreateModal.vue';
import AdditionalPaymentBulkStatusChangeModal from '@pages/additionalPayments/AdditionalPaymentBulkStatusChangeModal.vue';
import CreateAppPaymentConfirmModal from '@pages/additionalPayments/CreateAppPaymentConfirmModal.vue';
import DeleteAdditionalPaymentConfirmModal from '@pages/additionalPayments/DeleteAdditionalPaymentConfirmModal.vue';
import { useQueryRef } from '@/hooks/url';
import { useTableFields } from '@hooks/table-configuration';
import { FetchError, useFetchAction } from '@/hooks/fetch';
import { useNotify } from '@utils/notifications';
import { useDownloads } from '@/hooks/tasks';
import Tabs from '@components/structure/navigation/Tabs.vue';
import router from '@/router';

interface PagedResponse<T> {
  items: T[];
  total: number;
}

type AdditionalPaymentStatusTab = 'all' | 'open' | 'paid';
interface Props {
  variant?: AdditionalPaymentStatusTab;
}
const props = withDefaults(defineProps<Props>(), {
  variant: 'all',
});

const { t } = useI18n();
const notify = useNotify();

const collectionPath = '/payout_additional';
const csvTitle = t('navigation.additionalPayments.DEFAULT');

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const filter = useQueryRef('filter', {});

const sortKey = useQueryRef<string | undefined>('sortKey', 'recordedAt');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');

const totalFilter = computed<Record<string, unknown>>(() => {
  const baseFilter = (filter.value ?? {}) as Record<string, unknown>;
  if (props.variant === 'all') return baseFilter;
  return { ...baseFilter, status: props.variant };
});

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

const {
  data: items,
  loading,
  refetch,
} = useProvider<PagedResponse<PayoutAdditional>>(collectionPath, providerParams);

const [execAction, { loading: actionLoading }] = useFetchAction();
const createModalOpen = ref(false);
const bulkStatusModalOpen = ref(false);
const editModalOpen = ref(false);
const selectedForEdit = ref<PayoutAdditional | undefined>(undefined);
const checkedIdsList = ref<string[]>([]);
const downloads = useDownloads();
const appPaymentConfirmOpen = ref(false);
const appPaymentLoading = actionLoading;
const selectedForAppPayment = ref<PayoutAdditional | undefined>(undefined);
const deleteConfirmOpen = ref(false);
const deleteLoading = actionLoading;
const selectedForDelete = ref<PayoutAdditional | undefined>(undefined);
const painFileAccount = ref();
const painFileOptions = enumOptionsProvider('/enums/WBBankAccounts', {}, (item) => ({
  label: t(`toolbar.export.downloadPainFile.dropdown.${item}`),
  value: item,
}));
const reasonFilterOptions = enumOptionsProvider('/enums/PayoutAdditionalReason', {}, (item) => {
  const key = `pages.additionalPayments.reasonValues.${item}`;
  return {
    label: t(key),
    value: item,
  };
});
const statusFilterOptions = enumOptionsProvider('/enums/AdditionalPayoutStatusEnum', {}, (item) => {
  const key = `pages.additionalPayments.statusValues.${item}`;
  return {
    label: t(key),
    value: item,
  };
});

const variantForType = (type?: string): 'general' | 'campaign' =>
  type === 'campaign' ? 'campaign' : 'general';
const editModalVariant = computed<'general' | 'campaign'>(() =>
  variantForType(selectedForEdit.value?.paymentType));

const remove = (item: PayoutAdditional) => {
  selectedForDelete.value = item;
  deleteConfirmOpen.value = true;
};

const createAppPayment = (item: PayoutAdditional) => {
  selectedForAppPayment.value = item;
  appPaymentConfirmOpen.value = true;
};

const executeCreateAppPayment = async () => {
  if (!selectedForAppPayment.value) return;

  try {
    await execAction(
      `/payout_additional/${selectedForAppPayment.value.id}/create-app-payment`,
      { method: 'POST' },
    );
    appPaymentConfirmOpen.value = false;
    notify?.({ variant: 'success', title: t('pages.additionalPayments.appPaymentSuccess') });
    refetch();
  } catch {
    notify?.({ variant: 'error', title: t('pages.additionalPayments.appPaymentError') });
  } finally {
    selectedForAppPayment.value = undefined;
  }
};

const executeDelete = async () => {
  if (!selectedForDelete.value) return;

  try {
    await execAction(`/payout_additional/${selectedForDelete.value.id}`, { method: 'DELETE' });
    deleteConfirmOpen.value = false;
    notify?.({ variant: 'success', title: t('deleteSuccess') });
    refetch();
  } catch {
    notify?.({ variant: 'error', title: t('toolbar.export.errorRequest') });
  } finally {
    selectedForDelete.value = undefined;
  }
};

const onBulkStatusSaved = () => {
  checkedIdsList.value = [];
  refetch();
};

const viewAppPayment = (item: PayoutAdditional) => {
  const payoutFilterId = item.payoutId ?? item.paymentListId;
  if (!payoutFilterId) return;

  router.push({
    name: 'driver-payouts',
    params: { driverId: item.driverId },
    query: { filter: JSON.stringify({ id: payoutFilterId }) },
  });
};

const editItem = (item: PayoutAdditional) => {
  if (item.showInApp) {
    notify?.({ variant: 'warning', title: t('pages.additionalPayments.editDisabled.showInApp') });
    return;
  }
  selectedForEdit.value = item;
  editModalOpen.value = true;
};

const selectedRecords = computed(() => records.value.filter((x) => checkedIdsList.value.includes(x.id)));

const selectedPainPayments = computed(() =>
  selectedRecords.value.map((x) => ({
    id: x.id,
    type: x.paymentType ?? 'general',
  })));

const exportPainFile = async (ignoreAlreadyExported = false) => {
  if (!painFileAccount.value || selectedPainPayments.value.length === 0) return;
  try {
      const download = await execAction<Download>('/payout_additional/pain', {
      method: 'POST',
      body: {
        account: painFileAccount.value,
        ignoreAlreadyExported,
        payments: selectedPainPayments.value,
      },
    });
    downloads?.addTask(download);
    notify?.({ variant: 'success', title: t('toolbar.export.successRequest') });
    checkedIdsList.value = [];
    refetch();
  } catch (e) {
    if (e instanceof FetchError) {
      let errorKey: string | undefined;
      try {
        const errorCode = await e.response.clone().json();
        errorKey = errorCode?.error?.key ?? (typeof errorCode === 'string' ? errorCode : undefined);
      } catch {
        const errorText = await e.response.clone().text();
        errorKey = errorText?.trim();
      }
      if (errorKey === 'no_payouts') {
        notify?.({ variant: 'info', title: t('painExport.noPayouts') });
        return;
      }
      if (errorKey === 'already_exported' && !ignoreAlreadyExported) {
        const answer = await confirm?.(t('painExport.alreadyExportedConfirmation'));
        if (answer) await exportPainFile(true);
        return;
      }
    }
    notify?.({ variant: 'error', title: t('toolbar.export.errorRequest') });
  }
};

const actions: TableAction[] = [
  {
    title: t('pages.additionalPayments.actions.edit'),
    action: editItem,
    condition: (item) => !item.exported && !item.showInApp,
  },
  {
    title: t('pages.additionalPayments.actions.delete'),
    action: remove,
    condition: (item) => !item.showInApp && item.status !== 'paid',
  },
  {
    title: t('pages.additionalPayments.actions.createAppPayment'),
    action: createAppPayment,
    condition: (item) => !item.showInApp && !!item.campaignId,
  },
  {
    title: t('pages.additionalPayments.actions.viewAppPayment'),
    action: viewAppPayment,
    condition: (item) => item.showInApp && (!!item.payoutId || !!item.paymentListId),
  },
  {
    title: t('pages.additionalPayments.actions.exportedInfo'),
    action: () => notify?.({ variant: 'info', title: t('pages.additionalPayments.exportedNoActionsInfo') }),
    condition: (item) =>
      item.exported
      && item.status === 'paid'
      && !item.showInApp
      && !item.campaignId,
  },
];

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    sortable: true,
    visible: true,
    class: 'whitespace-nowrap',
    group: t('pages.additionalPayments.fieldGroups.general'),
  },
  {
    key: 'driverEmail',
    title: t('user.email'),
    sortable: true,
    visible: true,
    group: t('pages.additionalPayments.fieldGroups.general'),
    to: (item: PayoutAdditional) => ({ name: 'driver-payouts', params: { driverId: item.driverId } }),
    filter: {
      key: 'driver.user.email',
      type: 'text',
      group: t('pages.additionalPayments.filterGroups.general'),
      options: { label: t('user.email') },
    },
  },
  {
    key: 'driverFirstName',
    title: t('user.firstName'),
    sortable: true,
    visible: true,
    group: t('pages.additionalPayments.fieldGroups.general'),
    to: (item: PayoutAdditional) => ({ name: 'driver-payouts', params: { driverId: item.driverId } }),
    filter: {
      key: 'driver.user.firstName',
      type: 'text',
      group: t('pages.additionalPayments.filterGroups.general'),
      options: { label: t('user.firstName') },
    },
  },
  {
    key: 'driverLastName',
    title: t('user.lastName'),
    sortable: true,
    visible: true,
    group: t('pages.additionalPayments.fieldGroups.general'),
    to: (item: PayoutAdditional) => ({ name: 'driver-payouts', params: { driverId: item.driverId } }),
    filter: {
      key: 'driver.user.lastName',
      type: 'text',
      group: t('pages.additionalPayments.filterGroups.general'),
      options: { label: t('user.lastName') },
    },
  },
  {
    key: 'campaignName',
    title: t('campaign.DEFAULT'),
    sortable: true,
    visible: true,
    group: t('pages.additionalPayments.fieldGroups.general'),
    to: (item: PayoutAdditional) =>
      item.campaignId && item.campaignGroupId
        ? {
          name: 'campaign-group-payouts',
          params: { campaignGroupId: item.campaignGroupId },
          query: {
            filter: JSON.stringify({
              'campaignRegistration.campaign': item.campaignId,
            }),
          },
        }
        : undefined,
    filter: {
      key: 'campaign.name',
      type: 'text',
      group: t('pages.additionalPayments.filterGroups.general'),
      options: { label: t('campaign.DEFAULT') },
    },
  },
  {
    key: 'reason',
    title: t('pages.additionalPayments.reason'),
    sortable: true,
    visible: true,
    group: t('pages.additionalPayments.fieldGroups.general'),
    formatter: (v: string) => t(`pages.additionalPayments.reasonValues.${v}`),
    csvTranslations: {
      CampaignTopUp: t('pages.additionalPayments.reasonValues.CampaignTopUp'),
      CostParticipation: t('pages.additionalPayments.reasonValues.CostParticipation'),
      PostageCoverage: t('pages.additionalPayments.reasonValues.PostageCoverage'),
      HelperPaymentFlyering: t('pages.additionalPayments.reasonValues.HelperPaymentFlyering'),
      ReferralProgram: t('pages.additionalPayments.reasonValues.ReferralProgram'),
      LoyaltyPointsPayout: t('pages.additionalPayments.reasonValues.LoyaltyPointsPayout'),
      DepotPayout: t('pages.additionalPayments.reasonValues.DepotPayout'),
      HelperPaymentMounting: t('pages.additionalPayments.reasonValues.HelperPaymentMounting'),
      HelperPaymentVisibility: t('pages.additionalPayments.reasonValues.HelperPaymentVisibility'),
    },
    filter: {
      type: 'dropdown',
      group: t('pages.additionalPayments.filterGroups.general'),
      options: {
        label: t('pages.additionalPayments.reason'),
        options: reasonFilterOptions,
      },
    },
  },
  {
    key: 'status',
    title: t('pages.additionalPayments.status'),
    sortable: true,
    visible: true,
    formatter: (v: string) => t(`pages.additionalPayments.statusValues.${v}`),
    csvTranslations: {
      open: t('pages.additionalPayments.statusValues.open'),
      paid: t('pages.additionalPayments.statusValues.paid'),
    },
    group: t('pages.additionalPayments.fieldGroups.general'),
    filter: {
      type: 'dropdown',
      group: t('pages.additionalPayments.filterGroups.general'),
      options: {
        label: t('pages.additionalPayments.status'),
        options: statusFilterOptions,
      },
    },
  },
  {
    key: 'amountChf',
    title: t('pages.additionalPayments.amountChf'),
    sortable: true,
    visible: true,
    formatter: (v: number) => `${v ?? 0}`,
    group: t('pages.additionalPayments.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.additionalPayments.filterGroups.general'),
      options: { label: t('pages.additionalPayments.amountChf') },
    },
  },
  {
    key: 'recordedByName',
    title: t('pages.additionalPayments.recordedBy'),
    sortable: true,
    visible: true,
    group: t('pages.additionalPayments.fieldGroups.general'),
  },
  {
    key: 'recordedAt',
    title: t('pages.additionalPayments.recordedAt'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.additionalPayments.fieldGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.additionalPayments.filterGroups.general'),
      options: { label: t('pages.additionalPayments.recordedAt') },
    },
  },
  {
    key: 'showInApp',
    title: t('pages.additionalPayments.showInApp'),
    sortable: true,
    visible: true,
    formatter: (v: boolean) => (v ? t('yes') : t('no')),
    csvTranslations: {
      true: t('yes'),
      false: t('no'),
    },
    group: t('pages.additionalPayments.fieldGroups.general'),
    filter: {
      type: 'bool',
      group: t('pages.additionalPayments.filterGroups.general'),
      options: { label: t('pages.additionalPayments.showInApp') },
    },
  },
  {
    key: 'exported',
    title: t('pages.additionalPayments.exported'),
    sortable: true,
    visible: true,
    formatter: (v: boolean) => (v ? t('yes') : t('no')),
    csvTranslations: {
      true: t('yes'),
      false: t('no'),
    },
    group: t('pages.additionalPayments.fieldGroups.general'),
    filter: {
      type: 'bool',
      group: t('pages.additionalPayments.filterGroups.general'),
      options: { label: t('pages.additionalPayments.exported') },
    },
  },
  {
    key: 'createdAt',
    title: t('createdAt'),
    sortable: true,
    visible: false,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.additionalPayments.fieldGroups.general'),
  },
  {
    key: 'updatedAt',
    title: t('updatedAt'),
    sortable: true,
    visible: false,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.additionalPayments.fieldGroups.general'),
  },
];

const visibleColumns = useTableFields(columns);

const records = computed(() => unref(items)?.items ?? []);
const total = computed(() => unref(items)?.total ?? 0);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  }));
</script>

<style scoped lang="scss">
.table {
  @apply flex flex-col;
}
</style>
